import { PubSub } from 'graphql-subscriptions';
// `Resolvers` from generated/types is the full merged graph (matches merged
// typeDefs). `SetupMockResolver` passes the Premiere `Resolvers` shape into
// `initializeExecutableSchema`.
import type {
  LastViewedPath,
  PremiereExportPreset,
  PremiereProject,
  Resolvers,
  SharedAuthTokens,
  UxpHostInfo,
} from '../generated/types';
import {
  activeProject,
  exportPresets,
  getTheme,
  lastViewedPath,
  uxpHostInfo,
} from './mockData';
import type { PubSubEventMap } from './subscriptionPubSub';
import { SubscriptionEvents } from './subscriptionPubSub';

const pubsub = new PubSub<PubSubEventMap>();

export type DeepPartial<T> = T extends Record<string, unknown>
  ? { [K in keyof T]?: DeepPartial<T[K]> }
  : T;

type CallTracker = {
  count: number;
  calls: Array<{
    timestamp: number;
    args: unknown[];
  }>;
};

type UXPMocks = Record<string, unknown> & {
  callTracking?: Record<string, CallTracker>;
};

type UXPWindow = Window & { __UXP_MOCKS__?: UXPMocks };

const getUxpWindow = (): UXPWindow | undefined =>
  typeof window !== 'undefined' ? (window as UXPWindow) : undefined;

const getCustomMock = <T>(key: string): T | undefined => {
  const uxpWindow = getUxpWindow();
  if (uxpWindow?.__UXP_MOCKS__?.[key]) {
    return uxpWindow.__UXP_MOCKS__[key] as T;
  }
  return undefined;
};

/**
 * Creates a proxy that tracks the number of times a resolver function is
 * called and the arguments it was called with.
 * @param target - The object to track.
 * @param prefix - The prefix to use for the resolver name.
 * @returns A proxy that tracks calls to the target object.
 */
const createTrackedProxy = <T extends Record<string, unknown>>(
  target: T,
  prefix = ''
): T => {
  return new Proxy(target, {
    get(obj, prop) {
      const value = obj[prop as keyof T];

      if (typeof value === 'function') {
        return (...args: unknown[]) => {
          const resolverName = prefix
            ? `${prefix}.${String(prop)}`
            : String(prop);

          const uxpWindow = getUxpWindow();
          if (uxpWindow) {
            if (!uxpWindow.__UXP_MOCKS__) {
              uxpWindow.__UXP_MOCKS__ = {};
            }
            if (!uxpWindow.__UXP_MOCKS__.callTracking) {
              uxpWindow.__UXP_MOCKS__.callTracking = {};
            }
            if (!uxpWindow.__UXP_MOCKS__.callTracking[resolverName]) {
              uxpWindow.__UXP_MOCKS__.callTracking[resolverName] = {
                count: 0,
                calls: [],
              };
            }

            const tracker = uxpWindow.__UXP_MOCKS__.callTracking[resolverName];
            tracker.count += 1;
            tracker.calls.push({
              timestamp: Date.now(),
              args,
            });
          }

          return value(...args);
        };
      }

      if (typeof value === 'object' && value !== null) {
        return createTrackedProxy(
          value as Record<string, unknown>,
          prefix ? `${prefix}.${String(prop)}` : String(prop)
        );
      }

      return value;
    },
  });
};

const baseResolver = {
  Mutation: {
    signIn: () => {
      return true;
    },
    signOut: () => {
      return true;
    },
    refreshAccessToken: () => {
      const customMock = getCustomMock<string>('refreshAccessToken');
      if (customMock) {
        return customMock;
      }
      return '';
    },
    openPresetManager: () => {
      return true;
    },
    removeExportMetadata: () => {
      return [];
    },
    deleteExportMetadataForAsset: () => {
      return null;
    },
    openInBrowser: () => {
      const customMock = getCustomMock<boolean>('openInBrowser');
      if (customMock !== undefined) {
        return customMock;
      }
      return true;
    },
  },
  Query: {
    activeProject: async () => {
      const customMock = getCustomMock<PremiereProject>('activeProject');
      if (customMock) {
        return customMock;
      }
      return activeProject();
    },
    getTheme: () => {
      const customMock = getCustomMock<string>('getTheme');
      if (customMock) {
        return customMock;
      }
      return getTheme();
    },
    getLastViewedPath: () => {
      const customMock = getCustomMock<LastViewedPath>('getLastViewedPath');
      if (customMock) {
        return customMock;
      }
      return lastViewedPath();
    },
    isSignedIn: () => {
      return false;
    },
    getExportPresets: () => {
      const customMock =
        getCustomMock<Array<PremiereExportPreset>>('exportPresets');
      if (customMock) {
        return customMock;
      }
      return exportPresets();
    },
    getAuthTokens: () => {
      const customMock = getCustomMock<SharedAuthTokens>('getAuthTokens');
      if (customMock) {
        return customMock;
      }
      return {
        accessToken: '',
        refreshToken: '',
      };
    },
    getAllExportsXmpMetadata: () => {
      const customMock = getCustomMock<Array<unknown>>(
        'getAllExportsXmpMetadata'
      );
      if (customMock) {
        return customMock;
      }
      return [];
    },
    getAllCompositionExportsXmpMetadata: () => {
      const customMock = getCustomMock<Array<unknown>>(
        'getAllCompositionExportsXmpMetadata'
      );
      if (customMock) {
        return customMock;
      }
      return [];
    },
    activeAfterEffectsProject: () => {
      const customMock = getCustomMock<unknown>('activeAfterEffectsProject');
      if (customMock) {
        return customMock;
      }
      return null;
    },
    activeComposition: () => {
      const customMock = getCustomMock<unknown>('activeComposition');
      if (customMock) {
        return customMock;
      }
      return null;
    },
    getRenderQueueItemCount: () => {
      const customMock = getCustomMock<number>('getRenderQueueItemCount');
      if (customMock !== undefined) {
        return customMock;
      }
      return 0;
    },
    uxpHostInfo: () => {
      const customMock = getCustomMock<UxpHostInfo>('uxpHostInfo');
      if (customMock) {
        return customMock;
      }
      return uxpHostInfo();
    },
  },
  Subscription: {
    feedbackButtonTriggered: {
      subscribe: () => {
        return pubsub.asyncIterableIterator(SubscriptionEvents.FEEDBACK_BUTTON);
      },
    },
    sequenceEvents: {
      subscribe: () => {
        return pubsub.asyncIterableIterator(SubscriptionEvents.SEQUENCE_EVENTS);
      },
    },
    signInChanged: {
      subscribe: () => {
        return pubsub.asyncIterableIterator(
          SubscriptionEvents.ON_SIGN_IN_CHANGED
        );
      },
    },
    themeChanged: {
      subscribe: () => {
        return pubsub.asyncIterableIterator(SubscriptionEvents.THEME_CHANGED);
      },
    },
    importEvents: {
      subscribe: () => {
        return pubsub.asyncIterableIterator(SubscriptionEvents.IMPORT_EVENTS);
      },
    },
  },
};

export const mockResolver = createTrackedProxy(
  baseResolver
) as DeepPartial<Resolvers>;

export const CUSTOM_EVENTS = {
  THEME_CHANGE: 'mock-theme-change',
  SEQUENCE_EVENT: 'mock-sequence-event',
  FEEDBACK_BUTTON: 'mock-feedback-button',
  SIGN_IN_CHANGED: 'mock-sign-in-changed',
  IMPORT_EVENTS: 'mock-import-events',
} as const;

const handleThemeChange = (event: Event) => {
  const { themeChanged } = (event as CustomEvent).detail;
  pubsub.publish(SubscriptionEvents.THEME_CHANGED, { themeChanged });
};

const handleSequenceEvent = (event: Event) => {
  const { sequenceEvents } = (event as CustomEvent).detail;
  pubsub.publish(SubscriptionEvents.SEQUENCE_EVENTS, { sequenceEvents });
};

const handleFeedbackEvent = (event: Event) => {
  const { feedbackButtonTriggered } = (event as CustomEvent).detail;
  pubsub.publish(SubscriptionEvents.FEEDBACK_BUTTON, {
    feedbackButtonTriggered,
  });
};

const handleSignInChange = (event: Event) => {
  const { signInChanged } = (event as CustomEvent).detail;
  pubsub.publish(SubscriptionEvents.ON_SIGN_IN_CHANGED, { signInChanged });
};

const handleImportAssetProgress = (event: Event) => {
  const { importEvents } = (event as CustomEvent).detail;
  pubsub.publish(SubscriptionEvents.IMPORT_EVENTS, {
    importEvents,
  });
};

export const subscribeToSubscriptionMocks = (): void => {
  window.addEventListener(CUSTOM_EVENTS.THEME_CHANGE, handleThemeChange);
  window.addEventListener(CUSTOM_EVENTS.SEQUENCE_EVENT, handleSequenceEvent);
  window.addEventListener(CUSTOM_EVENTS.FEEDBACK_BUTTON, handleFeedbackEvent);
  window.addEventListener(CUSTOM_EVENTS.SIGN_IN_CHANGED, handleSignInChange);
  window.addEventListener(
    CUSTOM_EVENTS.IMPORT_EVENTS,
    handleImportAssetProgress
  );
};

export const unsubscribeFromSubscriptionMocks = (): void => {
  window.removeEventListener(CUSTOM_EVENTS.THEME_CHANGE, handleThemeChange);
  window.removeEventListener(CUSTOM_EVENTS.SEQUENCE_EVENT, handleSequenceEvent);
  window.removeEventListener(
    CUSTOM_EVENTS.FEEDBACK_BUTTON,
    handleFeedbackEvent
  );
  window.removeEventListener(CUSTOM_EVENTS.SIGN_IN_CHANGED, handleSignInChange);
  window.removeEventListener(
    CUSTOM_EVENTS.IMPORT_EVENTS,
    handleImportAssetProgress
  );
};
